// src/routes/ProtectedRoutes.js
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { useServiceRoleMembership } from "../hooks/useServiceRoleMembership";
import useServiceAccess from "../hooks/useServiceAccess";
import servicesConfig from "../config/ServicesConfig";
import { SERVICE_ENTITY_REGISTRY } from "../config/serviceEntityRegistry";

const ProtectedRoutes = ({
  children, allowedRoles = [], requiredPermission, serviceKey, dynamicRoleMembership,
  requiresInternalUser,
}) => {
  const {
    normalizedRoles, accessToken, loading, loadingUserData, pendingEntitySetups, hasEntityAccess,
    isSubscriptionRequired, hasActiveSubscription, fullUserData, hasServiceAccess, hasAnyNormalPermission,
  } = useAuth();
  const location = useLocation();
  // Pilot: Backend-Driven Route Membership (PERMISSIONS_IMPLEMENTATION_PLAN.md). Always called
  // (Rules of Hooks) — a no-op unless the route opts in via dynamicRoleMembership, in which case
  // it replaces the static allowedRoles check below for this route only.
  const { loading: roleMembershipLoading, isMember: hasServiceRoleMembership } =
    useServiceRoleMembership(dynamicRoleMembership ? serviceKey : null);
  // Admin-configured per-service, per-plan-tier accessibility level (System Admin's Service
  // Access screen, "No Plan" tab included). Used below so the blanket subscription redirect only
  // fires when this exact service is actually configured to need one - a service the admin set
  // to FULL/READ_ONLY for "No Plan" should stay reachable without an active
  // subscription instead of being blocked the same way every service used to be.
  const { level: serviceAccessLevel } = useServiceAccess(
    serviceKey ? servicesConfig[serviceKey]?.accessKey : null
  );

  // Wait for auth initialization before making any access decisions
  if (loading) return null;

  // Wait for full user data so pendingEntitySetup / subscription status can be accurately computed
  if (loadingUserData) return null;

  // If no token, redirect to login
  if (!accessToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Coming Soon short-circuits before everything else — subscription/access/Dashboard-permission
  // are irrelevant for a service the frontend hasn't built yet. See SERVICE_DASHBOARD_NAVIGATION_PLAN.md §3.7.
  if (serviceKey && servicesConfig[serviceKey]?.isImplemented === false) {
    return <Navigate to={`/home/coming-soon?service=${serviceKey}`} replace />;
  }

  // A service's entity-setup route is always reachable regardless of hasServiceAccess - it's
  // reached precisely to let an account that doesn't have access YET obtain it (e.g. an
  // Individual account redirected here for a cross-usertype subscribe - see the cross-service
  // access plan's Case 2). The role is only ever assigned at approval time, after this form is
  // submitted, so gating the form itself on already holding it would make it unreachable for
  // exactly the account it exists for. Computed before both subscription gates below - the
  // blanket "needs an active subscription" gate never carried this exception, only the
  // hasServiceAccess gate further down did. That meant ANY account with zero PERSONAL
  // subscription rows (the normal, expected state for a member - hasActiveSubscription() checks
  // the account's own userSubscriptions, never what a tenant admin holds) could never reach the
  // entity-setup form for a cross-usertype service at all, direct URL entry included - bounced to
  // the generic plan carousel every time instead, which grants no actual access to the service
  // being set up (no companyEntity/fpoEntity, no role) even if a plan there gets approved.
  // Confirmed live: an FPO member navigating straight to /mybusiness/setup landed on "Choose a
  // Plan" instead.
  const isEntitySetupPath = Object.values(SERVICE_ENTITY_REGISTRY).some(
    (config) => config.setupRoute === location.pathname
  );

  // A route with no serviceKey has no per-service accessibility rule to consult, so it keeps
  // the old blanket behavior. A route that DOES have one only redirects when that service is
  // actually configured as SUBSCRIBE_REQUIRED (or HIDDEN) for the user's current plan tier
  // (including "No Plan") - FULL/READ_ONLY there means the admin deliberately wants
  // this service reachable without an active subscription.
  const blocksWithoutSubscription = !serviceKey
    || serviceAccessLevel === "SUBSCRIBE_REQUIRED"
    || serviceAccessLevel === "HIDDEN";
  if (fullUserData && !normalizedRoles.systemAdmin && isSubscriptionRequired() && !hasActiveSubscription() && blocksWithoutSubscription && !isEntitySetupPath) {
    const svcKeyFromPath = location.pathname.split("/").filter(Boolean)[0];
    return <Navigate to={`/home/subscription${svcKeyFromPath ? `?service=${svcKeyFromPath}` : ""}`} replace />;
  }

  // Enforce service permission for direct URLs as well as dashboard clicks.
  if (serviceKey && !normalizedRoles.systemAdmin && !hasServiceAccess(serviceKey) && !isEntitySetupPath) {
    return <Navigate to="/home" replace />;
  }

  // Dashboard-permission gate — additive to hasServiceAccess above, scoped to the exact service
  // landing path only (never nested module routes, which stay governed by their own
  // requiredPermission/allowedRoles below, untouched). See SERVICE_DASHBOARD_NAVIGATION_PLAN.md §4.
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isServiceLandingPage = pathSegments.length === 1;
  if (serviceKey && isServiceLandingPage && !hasAnyNormalPermission(servicesConfig[serviceKey]?.dashboardPermissions)) {
    // Accounts that get here already passed hasServiceAccess above (they have real
    // accessControls-backed access) — they're just missing a normalPermissions.*Dashboard flag
    // (e.g. viewer-tier roles, which never carry one). Land them on the service's About page
    // instead of the bare summary screen when one exists — it only requires hasServiceAccess
    // (allowedRoles: [] on that route), so it's already reachable for these accounts.
    const aboutRoute = servicesConfig[serviceKey]?.aboutRoute;
    return <Navigate to={aboutRoute || `/home/service-summary?service=${serviceKey}`} replace />;
  }

  // Redirect to service entity setup if required — must run before allowedRoles early-return.
  // pendingEntitySetups can hold more than one eligible-and-missing service at once, so find the
  // one (if any) that actually belongs to the route being entered, rather than assuming there's
  // only ever one pending setup — a single-match assumption here previously let one service's
  // pending setup mask another's (an account eligible-and-missing on both MY_ORG and MY_BUSINESS
  // was never redirected into /mybusiness/setup because MY_ORG's entry came first).
  const matchingEntitySetup = pendingEntitySetups?.find((config) =>
    location.pathname.startsWith(config.routePrefix)
  );
  if (matchingEntitySetup && !location.pathname.includes("/setup")) {
    return <Navigate to={matchingEntitySetup.setupRoute} replace />;
  }

  // Check role membership first (unset/empty allowedRoles = any authenticated user).
  // dynamicRoleMembership routes (pilot, see hooks/useServiceRoleMembership.js) skip the
  // static array entirely and resolve membership from ServiceRoleMappingEntity instead —
  // the two mechanisms are not combined on the same route.
  if (dynamicRoleMembership) {
    if (roleMembershipLoading) return null;
    if (!hasServiceRoleMembership) {
      return <Navigate to="/home" replace />;
    }
  } else if (allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => normalizedRoles[role]);
    if (!hasRequiredRole) {
      return <Navigate to="/home" replace />;
    }
  }

  if (requiredPermission && !normalizedRoles.systemAdmin) {
    const { entityKey, action } = requiredPermission;
    if (!hasEntityAccess(entityKey, action)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Area-level lock independent of accessControls — real data (fullUserData.userTypeEntity.
  // internalUser), not a role-name check, so it stays correct for any current or future
  // internal-usage role/userType. systemAdmin's own userType already has internalUser:true, but
  // bypassed explicitly anyway to match every other check in this file.
  // fullUserData && prerequisite matches the subscription check above — fails open on the brief
  // render where loadingUserData hasn't flipped true yet but fullUserData is still null; once it
  // loads, this re-evaluates correctly on the next render (same race pendingEntitySetup guards
  // against with its own `if (!fullUserData) return null` above).
  if (requiresInternalUser && fullUserData && !normalizedRoles.systemAdmin) {
    if (fullUserData.userTypeEntity?.internalUser !== true) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  return children;
};

export default ProtectedRoutes;
